import { useEffect, useMemo, type ReactNode } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  Textarea,
} from '@monksflow/monks-ui';
import {
  buildCustomizationFormValues,
  EDITABLE_SPRINGBOARD_CATEGORY_IDS,
  getSpringboardTabById,
  SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
  SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH,
  SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
  toSpringboardCategoryOverrides,
  useSaveSpringboardCategories,
  useSpringboardCategoryCustomization,
} from '@/features/springboard';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AdminPageShell } from '../AdminPageShell';

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
const buildFormSchema = (t: TFunction<'admin'>) =>
  z.object({
    categories: z
      .array(
        z.object({
          id: z.enum(EDITABLE_SPRINGBOARD_CATEGORY_IDS),
          label: z
            .string()
            .trim()
            .max(
              SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH,
              t('admin.customization.labelMaxLength', {
                max: SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH,
              }),
            ),
          subtitle: z
            .string()
            .trim()
            .max(
              SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
              t('admin.customization.subtitleMaxLength', {
                max: SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
              }),
            ),
          description: z
            .string()
            .trim()
            .max(
              SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
              t('admin.customization.descriptionMaxLength', {
                max: SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
              }),
            ),
        }),
      )
      .length(4),
  });

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

/* eslint-disable i18next/no-literal-string -- form field names; labelKey carries the copy */
const CATEGORY_FIELDS = [
  { name: 'label', labelKey: 'admin.customization.label', multiline: false },
  { name: 'subtitle', labelKey: 'admin.customization.subtitle', multiline: false },
  { name: 'description', labelKey: 'admin.customization.description', multiline: true },
] as const;
/* eslint-enable i18next/no-literal-string */

/** The page's title and its legacy white card, bound once for all three states. */
function CustomizationShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('admin');

  return <AdminPageShell title={t('admin.customization.customization')}>{children}</AdminPageShell>;
}

export const CustomizationPage = () => {
  const { t } = useTranslation('admin');

  const formSchema = useMemo(() => buildFormSchema(t), [t]);

  const { data, isLoading, isError } = useSpringboardCategoryCustomization();
  const saveMutation = useSaveSpringboardCategories();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: buildCustomizationFormValues(),
    mode: 'onTouched',
  });
  const { fields } = useFieldArray({ control: form.control, name: 'categories' });

  useEffect(() => {
    // Only sync server data into the form when the user has no pending edits.
    // A background refetch (e.g. refetch-on-focus surfacing a concurrent edit's
    // new updatedAt) produces a fresh `data` reference; without this guard the
    // reset would silently discard the admin's unsaved changes.
    if (!data || form.formState.isDirty) {
      return;
    }

    form.reset(buildCustomizationFormValues(data));
  }, [data, form]);

  const handleSubmit = form.handleSubmit((formData) => {
    saveMutation.mutate(toSpringboardCategoryOverrides(formData.categories), {
      onSuccess: (response) => {
        form.reset(buildCustomizationFormValues(response));
        notificationService.showSuccess(t('admin.customization.customizationSaved'));
      },
    });
  });

  if (isLoading) {
    return (
      <CustomizationShell>
        <div className="flex min-h-[320px] items-center justify-center">
          <Spinner />
        </div>
      </CustomizationShell>
    );
  }

  if (isError) {
    return (
      <CustomizationShell>
        <p className="text-sm text-destructive">
          {t('admin.customization.failedToLoadCustomizationSettings')}
        </p>
      </CustomizationShell>
    );
  }

  return (
    <CustomizationShell>
      <div className="flex flex-col gap-1 mb-6">
        <p className="text-sm text-gray-600">
          {t('admin.customization.customizeTheLabelsAndDescriptions')}
        </p>
        <p className="text-sm text-gray-600">{t('admin.customization.emptyFieldsUseDefault')}</p>
      </div>

      <Form {...form}>
        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {fields.map((field, index) => {
              const categoryId = form.getValues(`categories.${index}.id`);
              const defaults = getSpringboardTabById(categoryId);
              return (
                <div
                  key={field.id}
                  className="min-w-0 flex flex-col border border-gray-200 rounded-lg p-4"
                >
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 shrink-0">
                    {categoryId.toUpperCase()}
                  </h3>
                  <div className="flex flex-col gap-4 flex-1">
                    {CATEGORY_FIELDS.map(({ name, labelKey, multiline }) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={`categories.${index}.${name}`}
                        render={({ field: controllerField }) => (
                          <FormItem>
                            <FormLabel>{t(labelKey)}</FormLabel>
                            <FormControl>
                              {multiline ? (
                                <Textarea
                                  {...controllerField}
                                  rows={3}
                                  placeholder={defaults?.[name]}
                                />
                              ) : (
                                <Input
                                  {...controllerField}
                                  className="bg-background"
                                  placeholder={defaults?.[name]}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end mt-6">
            <Button type="submit" variant="default" disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Spinner aria-hidden />}
              {saveMutation.isPending
                ? t('admin.customization.saving')
                : t('admin.customization.saveChanges')}
            </Button>
          </div>
        </form>
      </Form>
    </CustomizationShell>
  );
};
